/**
 * PendingTasksContext
 *
 * Holt die „Wartet auf mich"-Counts vom Backend (`/api/dashboard/my-pending-tasks`)
 * und stellt sie global zur Verfügung – wird vom Dashboard-Widget UND von der
 * Sidebar genutzt. Auto-Refresh alle 60 s + beim Tab-Visibility-Change.
 *
 * Shape:
 *   { total: number, items: [{ category, label, count, link, icon }], byPath: { [link]: count } }
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const PendingTasksContext = createContext({
  total: 0,
  items: [],
  byPath: {},
  unreadNotifications: 0,
  newJobsCount: 0,
  newNewsCount: 0,
  reload: () => {},
});

export const PendingTasksProvider = ({ children }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ total: 0, items: [], byPath: {}, unreadNotifications: 0, newJobsCount: 0, newNewsCount: 0 });

  const load = useCallback(async () => {
    if (!user) {
      setData({ total: 0, items: [], byPath: {}, unreadNotifications: 0, newJobsCount: 0, newNewsCount: 0 });
      return;
    }
    try {
      const [pendingRes, jobsRes, newsRes] = await Promise.all([
        api.get('/dashboard/my-pending-tasks'),
        api.get('/jobs/new-count').catch(() => ({ data: { count: 0 } })),
        api.get('/news/new-count').catch(() => ({ data: { count: 0 } })),
      ]);
      const res = pendingRes;
      const items = res.data?.items || [];
      const byPath = items.reduce((acc, it) => {
        acc[it.link] = it.count;
        return acc;
      }, {});
      setData({
        total: res.data?.total || 0,
        items,
        byPath,
        unreadNotifications: res.data?.unread_notifications || 0,
        newJobsCount: jobsRes.data?.count || 0,
        newNewsCount: newsRes.data?.count || 0,
      });
    } catch {
      setData({ total: 0, items: [], byPath: {}, unreadNotifications: 0, newJobsCount: 0, newNewsCount: 0 });
    }
  }, [user]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [load]);

  return (
    <PendingTasksContext.Provider value={{ ...data, reload: load }}>
      {children}
    </PendingTasksContext.Provider>
  );
};

export const usePendingTasks = () => useContext(PendingTasksContext);
